'use client';

import { useState } from 'react';
import Link from 'next/link';
import Script from 'next/script';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import type { User } from '@supabase/supabase-js';
import type { SubscriptionStatus } from '@/types';
import {
  CASE_PASS_OPTIONS,
  type CasePassTermMonths,
} from '@/lib/billing/case-pass-constants';
import {
  PROFESSIONAL_ANNUAL_PLANS,
  type ProfessionalAnnualPlanId,
} from '@/lib/billing/plans';
import { isCasePassCheckoutAvailable } from '@/lib/billing/checkout-eligibility';
import { getRazorpay, loadRazorpayCheckoutScript } from '@/lib/billing/razorpay-client';
import { getCheckoutAttribution } from '@/lib/attribution';
import { analyticsEvents } from '@/components/analytics/Analytics';
import { NEW_PRICING_FAQ_ITEMS } from './tierData';
import { PaymentTrustBadges } from './PaymentTrustBadges';
import PricingFAQ from './PricingFAQ';

interface NewPricingContentProps {
  subStatus: SubscriptionStatus | null;
  isAuthenticated: boolean;
  user: User | null;
}

function formatINR(value: number): string {
  return `₹${value.toLocaleString('en-IN')}`;
}

const CHECK_ICON = (
  <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

function FeatureList({ features }: { features: string[] }) {
  return (
    <ul className="mt-6 space-y-2.5">
      {features.map((feature) => (
        <li key={feature} className="flex items-start gap-2.5 text-sm text-slate-700">
          {CHECK_ICON}
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}

function CardShell({
  highlight,
  badge,
  children,
}: {
  highlight?: boolean;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`relative flex flex-col rounded-2xl border bg-white p-7 transition-shadow ${
        highlight ? 'border-indigo-500 ring-2 ring-indigo-500/10 shadow-lg shadow-indigo-500/10' : 'border-slate-200'
      }`}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1 text-[11px] font-semibold text-white shadow-sm">
            {badge}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

export default function NewPricingContent({
  subStatus,
  isAuthenticated,
  user,
}: NewPricingContentProps) {
  const router = useRouter();
  const [razorpayLoaded, setRazorpayLoaded] = useState(() => Boolean(getRazorpay()));
  const [casePassSubmittingTerm, setCasePassSubmittingTerm] = useState<CasePassTermMonths | null>(null);
  const [selectedVolumePlanId, setSelectedVolumePlanId] = useState<ProfessionalAnnualPlanId>(
    PROFESSIONAL_ANNUAL_PLANS[0].id,
  );
  const [volumePlanSubmitting, setVolumePlanSubmitting] = useState(false);
  const loginRedirect = `/login?redirect=${encodeURIComponent('/pricing')}`;
  const selectedVolumePlan = PROFESSIONAL_ANNUAL_PLANS.find(
    (plan) => plan.id === selectedVolumePlanId,
  ) || PROFESSIONAL_ANNUAL_PLANS[0];

  async function handleBuyCasePass(termMonths: CasePassTermMonths) {
    const selectedProduct = CASE_PASS_OPTIONS.find((option) => option.termMonths === termMonths);
    if (!selectedProduct) return;
    setCasePassSubmittingTerm(termMonths);
    try {
      const checkoutReady = razorpayLoaded || await loadRazorpayCheckoutScript();
      const Razorpay = getRazorpay();
      if (!checkoutReady || !Razorpay) {
        toast.error('Payment system could not load. Please refresh the page and try again.');
        setCasePassSubmittingTerm(null);
        return;
      }

      const res = await fetch('/api/case-pass/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ term_months: termMonths }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Failed to start checkout');
        setCasePassSubmittingTerm(null);
        return;
      }

      const paidAmountInr = (typeof data.amount === 'number' ? data.amount : selectedProduct.priceInr * 100) / 100;
      analyticsEvents.subscriptionInitiated(paidAmountInr, data.currency || 'INR', 'case_pass');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const options: any = {
        key: data.key_id,
        order_id: data.razorpay_order_id,
        amount: data.amount,
        currency: data.currency || 'INR',
        name: 'India Case Status',
        description: `${selectedProduct.name} — 1 case, ${selectedProduct.termMonths} months`,
        handler: async (response: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => {
          try {
            const verifyRes = await fetch('/api/subscription/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                pass_id: data.pass_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const verifyBody = await verifyRes.json();
            const activated = verifyRes.ok && (verifyBody.success || verifyBody.reason === 'already_active');
            if (!activated) {
              toast.error('Could not verify payment. Please contact support.');
              setCasePassSubmittingTerm(null);
              return;
            }

            // Bounded await (~2s timeout inside); unsent conversions persist
            // to sessionStorage and re-fire on the next page load.
            await analyticsEvents.subscriptionPurchased(
              paidAmountInr,
              data.currency || 'INR',
              response.razorpay_payment_id,
              'case_pass',
            );

            const unlockedCaseId = typeof verifyBody.case_id === 'string' && verifyBody.case_id
              ? verifyBody.case_id
              : null;
            if (unlockedCaseId) {
              toast.success('Case Pass activated! Opening your case…');
              router.push(`/case/${unlockedCaseId}?unlocked=1`);
            } else {
              toast.success('Case Pass activated! Redirecting to dashboard...');
              router.push('/dashboard');
            }
          } catch {
            toast.error('Payment verification failed. Please contact support.');
            setCasePassSubmittingTerm(null);
          }
        },
        prefill: { contact: user?.phone || '', email: user?.email || '' },
        theme: { color: '#4F46E5' },
        modal: { ondismiss: () => setCasePassSubmittingTerm(null) },
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', (response: { error: { description: string } }) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setCasePassSubmittingTerm(null);
      });
      rzp.open();
    } catch {
      toast.error('Something went wrong. Please try again.');
      setCasePassSubmittingTerm(null);
    }
  }

  async function handleBuyVolumePlan(planId: ProfessionalAnnualPlanId) {
    const selectedPlan = PROFESSIONAL_ANNUAL_PLANS.find((plan) => plan.id === planId);
    if (!selectedPlan) return;

    setVolumePlanSubmitting(true);
    try {
      const checkoutReady = razorpayLoaded || await loadRazorpayCheckoutScript();
      const Razorpay = getRazorpay();
      if (!checkoutReady || !Razorpay) {
        toast.error('Payment system could not load. Please refresh the page and try again.');
        setVolumePlanSubmitting(false);
        return;
      }

      const attribution = getCheckoutAttribution({
        source_surface: 'pricing_page',
        plan_id: selectedPlan.id,
      });
      const res = await fetch('/api/subscription/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: selectedPlan.id,
          currency: 'INR',
          attribution,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.checkout_mode !== 'order') {
        toast.error(data.error || 'Failed to start checkout');
        setVolumePlanSubmitting(false);
        return;
      }

      const paidAmountInr = (typeof data.amount === 'number'
        ? data.amount
        : selectedPlan.priceYearlyInr * 100) / 100;
      analyticsEvents.subscriptionInitiated(paidAmountInr, data.currency || 'INR', selectedPlan.id);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const options: any = {
        key: data.key_id,
        order_id: data.razorpay_order_id,
        amount: data.amount,
        currency: data.currency || 'INR',
        name: 'India Case Status',
        description: `${selectedPlan.caseLimit} cases — 12 months, one-time payment`,
        handler: async (response: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => {
          try {
            const verifyRes = await fetch('/api/subscription/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                subscription_id: data.subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                attribution,
              }),
            });
            const verifyBody = await verifyRes.json();
            if (!verifyRes.ok || !verifyBody.success) {
              if (verifyBody.internationalCardDetected) {
                toast.error('Please use UPI or an Indian card for this INR checkout.');
              } else {
                toast.error(verifyBody.error || 'Could not verify payment. Please contact support.');
              }
              setVolumePlanSubmitting(false);
              return;
            }

            await analyticsEvents.subscriptionPurchased(
              paidAmountInr,
              data.currency || 'INR',
              response.razorpay_payment_id,
              selectedPlan.id,
            );
            toast.success(`${selectedPlan.caseLimit}-case annual plan activated!`);
            router.push('/dashboard');
          } catch {
            toast.error('Payment verification failed. Please contact support.');
            setVolumePlanSubmitting(false);
          }
        },
        prefill: { contact: user?.phone || '', email: user?.email || '' },
        theme: { color: '#4F46E5' },
        modal: { ondismiss: () => setVolumePlanSubmitting(false) },
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', (response: { error: { description: string } }) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setVolumePlanSubmitting(false);
      });
      rzp.open();
    } catch {
      toast.error('Something went wrong. Please try again.');
      setVolumePlanSubmitting(false);
    }
  }

  const casePassAvailableForUser = isCasePassCheckoutAvailable(isAuthenticated);

  return (
    <>
      <Script
        src="https://checkout.razorpay.com/v1/checkout.js"
        strategy="afterInteractive"
        onReady={() => setRazorpayLoaded(Boolean(getRazorpay()))}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        <section className="relative overflow-hidden px-4 pt-20 pb-10 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-indigo-600">Simple pricing</p>
            <h1 className="mt-3 text-4xl font-bold text-slate-900 sm:text-5xl">
              One case or a plan built for you
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
              Choose a Case Pass for one matter or an annual plan for 5 to 100 cases. Custom pricing remains available for larger teams and tailored workflows.
            </p>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-500">
              Every account still includes one case saved and checked for free.
            </p>
          </div>
        </section>

        {subStatus?.hasActiveSubscription && (
          <div className="mx-auto max-w-5xl px-4 pb-6 sm:px-6 lg:px-8">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center text-sm text-emerald-700">
              You&apos;re on the <strong>{subStatus.subscription?.plan_name || 'Active'}</strong> plan
              {subStatus.daysRemaining != null && ` · ${subStatus.daysRemaining} days remaining`}
            </div>
          </div>
        )}

        <section className="px-4 pb-14 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {CASE_PASS_OPTIONS.map((option) => (
              <CardShell
                key={option.id}
                highlight={option.id === 'yearly'}
                badge={option.id === 'yearly' ? 'Best value' : 'For one case'}
              >
                <h2 className="text-xl font-semibold text-slate-900">{option.name}</h2>
                <p className="mt-1 text-sm text-slate-500">One-time payment for one case</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-slate-900">{formatINR(option.priceInr)}</span>
                  <span className="text-sm text-slate-500">one-time</span>
                </div>
                <p className="mt-2 text-xs font-medium text-slate-600">
                  No autopay. Full tracking and alerts for {option.termMonths} months from activation.
                </p>

                <div className="mt-6">
                  {!isAuthenticated ? (
                    <Link
                      href={loginRedirect}
                      className="block w-full rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-shadow hover:shadow-md"
                    >
                      Log in to buy
                    </Link>
                  ) : casePassAvailableForUser ? (
                    <button
                      type="button"
                      onClick={() => handleBuyCasePass(option.termMonths)}
                      disabled={casePassSubmittingTerm !== null}
                      className="block w-full rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-shadow hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {casePassSubmittingTerm === option.termMonths
                        ? 'Processing…'
                        : `Buy for ${formatINR(option.priceInr)}`}
                    </button>
                  ) : null}
                </div>

                <FeatureList
                  features={[
                    'Live status with no monthly refresh cap',
                    'WhatsApp and email alerts on every update',
                    'Full order and hearing history',
                    'Change the paid case once during the term',
                    `Fixed ${option.termMonths}-month term from activation`,
                  ]}
                />
              </CardShell>
            ))}

            <CardShell badge="For higher volume">
              <h2 className="text-xl font-semibold text-slate-900">Annual Multi-Case Plan</h2>
              <p className="mt-1 text-sm text-slate-500">One-time annual payment for multiple cases</p>

              <label htmlFor="annual-case-volume" className="mt-6 text-xs font-semibold uppercase tracking-wide text-slate-600">
                Cases to track
              </label>
              <select
                id="annual-case-volume"
                value={selectedVolumePlan.id}
                onChange={(event) => setSelectedVolumePlanId(event.target.value as ProfessionalAnnualPlanId)}
                disabled={volumePlanSubmitting}
                className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-sm font-semibold text-slate-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {PROFESSIONAL_ANNUAL_PLANS.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.caseLimit} cases — {formatINR(plan.priceYearlyInr)} / year
                  </option>
                ))}
              </select>

              <div className="mt-6 flex items-baseline gap-2" aria-live="polite">
                <span className="text-4xl font-bold text-slate-900">
                  {formatINR(selectedVolumePlan.priceYearlyInr)}
                </span>
                <span className="text-sm text-slate-500">one-time</span>
              </div>
              <p className="mt-2 text-xs font-medium text-slate-600">
                No autopay. Track up to {selectedVolumePlan.caseLimit} active cases for 12 months.
              </p>

              <div className="mt-6">
                {!isAuthenticated ? (
                  <Link
                    href={loginRedirect}
                    className="block w-full rounded-lg bg-slate-900 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
                  >
                    Log in to buy
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleBuyVolumePlan(selectedVolumePlan.id)}
                    disabled={volumePlanSubmitting}
                    className="block w-full rounded-lg bg-slate-900 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {volumePlanSubmitting
                      ? 'Processing…'
                      : `Buy for ${formatINR(selectedVolumePlan.priceYearlyInr)}`}
                  </button>
                )}
              </div>

              <FeatureList
                features={[
                  `Up to ${selectedVolumePlan.caseLimit} active monitored cases`,
                  'WhatsApp and email alerts on every update',
                  'Full order and hearing history',
                  'Bulk case onboarding',
                  'Fixed 12-month term from activation',
                ]}
              />
            </CardShell>

            <CardShell>
              <h2 className="text-xl font-semibold text-slate-900">Custom Pricing</h2>
              <p className="mt-1 text-sm text-slate-500">For more than 100 cases, teams, and tailored workflows</p>
              <div className="mt-6">
                <span className="text-3xl font-bold text-slate-900">Request access</span>
              </div>
              <p className="mt-2 text-xs font-medium text-slate-600">
                Tell us your case volume and workflow. We&apos;ll recommend one clear plan for your needs.
              </p>

              <div className="mt-6">
                <Link
                  href="/request-access?utm_source=pricing&utm_medium=pricing_page&utm_campaign=custom_plan"
                  className="block w-full rounded-lg border border-slate-900 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-50"
                >
                  Contact us for pricing
                </Link>
              </div>

              <FeatureList
                features={[
                  'Caseloads beyond the self-serve options',
                  'Advocate, firm, and in-house team workflows',
                  'Bulk case onboarding',
                  'Team access and tailored support',
                  'Billing matched to your requirements',
                ]}
              />
            </CardShell>
          </div>

          <div className="mx-auto mt-8 flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">{CHECK_ICON}All prices include GST</span>
            <span className="flex items-center gap-1.5">{CHECK_ICON}No autopay on Case Passes or annual plans</span>
          </div>
          <PaymentTrustBadges oneTimeOnly />
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <PricingFAQ items={NEW_PRICING_FAQ_ITEMS} />
        </section>

        <section className="border-t border-slate-200 bg-slate-900 px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Start with your case today.
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300">
              Track one case free, choose a Case Pass, or select an annual plan for up to 100 cases.
            </p>
            <div className="mt-6">
              <Link
                href={isAuthenticated ? '/dashboard' : '/login?redirect=/add-case'}
                className="inline-block rounded-lg bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100"
              >
                Track one case free
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
